import { Button } from '@/components/button';
import { ProviderListCard } from './provider-list-card';
import { Section } from '@/components/containers/section';
import styles from './selected-provider.module.css';
import { useGetProvider } from '@/api/get-provider-info';
import { useEncounter } from '@/contexts/encounter-context';
import { SectionHeader } from '@/components/section-header';
import { VisitFormButtonRow } from '@/components/containers/visit-form-button-row';
import { SelectProviderButtonContainer } from './select-provider-button-container';
import { Navigate } from '@/util/react-router-dom';
import { NextAvailableProviderListCard } from './next-available-provider-list-card';
import { Loader } from '@/components/loaders/loader';
import { useTranslation } from '@/contexts/i18n-context';

const translationBase = 'visit.providerSelection.selectedProvider';

interface SelectedProviderProps {
  providerId?: string | null;
  onEdit: () => void;
}

export function SelectedProvider({ providerId, onEdit }: SelectedProviderProps) {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const { data, isFetching } = useGetProvider(
    providerId!,
    { encounterId: encounter.id },
    { query: { enabled: !!providerId } },
  );

  if (!isFetching && providerId && !data?.provider) {
    return <Navigate to="/not-found" />;
  }

  const unavailableProvider = Boolean(
    data?.provider && (data.provider.nextAvailableDate || !data.provider.isActive),
  );

  return (
    <>
      <Section className={styles.form}>
        <SectionHeader
          title={t(`${translationBase}.title`)}
          className={styles.sectionHeader}
          editButton={
            <Button variant="link" leftIcon="edit" onClick={onEdit}>
              {t(`${translationBase}.editButton`)}
            </Button>
          }
        />
        {isFetching ? (
          <Loader type="small" />
        ) : !providerId ? (
          <NextAvailableProviderListCard />
        ) : (
          data?.provider && <ProviderListCard provider={data.provider} showSelectButton={false} />
        )}
      </Section>
      <SelectProviderButtonContainer provider={data?.provider}>
        {(onClick, loading) => (
          <VisitFormButtonRow
            rightIcon="arrow_forward"
            nextStep={t(`${translationBase}.nextStep`)}
            buttonTextKey="common.next"
            disabled={unavailableProvider}
            onClick={onClick}
            type="submit"
            loading={loading}
          />
        )}
      </SelectProviderButtonContainer>
    </>
  );
}
